import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAccountToDisplaySelector } from "../../redux/actions/user";
import StepRow from "./modules/OnboardStepRow/StepRow";
import {
  jetpackDashboardSteps,
  STEP_0,
  STEP_1_LOGO_COLORS,
  STEP_2_GIFTS,
  STEP_3_GAMES,
  STEP_4_CTAS,
  STEP_5_MKTG_AUTO,
  STEP_6_POSA
} from "./constants";
import { type Step } from "../../components/Stepper/Stepper";
import TestsID from "../../utils/TestsID";
import { useHistory } from "react-router-dom";
import "./styles.scss";
import { useJetpackConfigSelector } from "../../redux/jetpackConfig";
import UpsellBanner from "./components/UpsellBanner";
import KC_Separator from "../../designsystem/KC_Separator/KC_Separator";
import { KC_Sizes } from "../../designsystem/ds_commons";
import { checkCTAsAreComplete } from "../../utils/helpers/checkCTAsAreComplete";
import { buildPathWithTrackingId } from "../../utils/routing/routingUtils";
import { filterStepsByFeatures } from "../../utils/helpers/filterStepsByFeatures";
import { useSubscriptionPlansSelector } from "../../redux/actions/app";

export default function JetpackConfigurator() {
  const history = useHistory();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const jetpackConfig = useJetpackConfigSelector();

  const [stepsCompletion, setStepsCompletion] = useState<
    Record<number, boolean>
  >({});

  function isStepCompleted(step: Step): boolean {
    switch (step.index) {
      case STEP_0:
        return false;

      case STEP_1_LOGO_COLORS:
        return (
          !!jetpackConfig?.logo_url &&
          !!jetpackConfig?.colors?.background &&
          !!jetpackConfig?.colors?.accent &&
          (!!jetpackConfig?.colors?.titles || !!jetpackConfig?.colors?.primary)
        );

      case STEP_2_GIFTS:
        return (
          !(jetpackConfig == null) &&
          !!jetpackConfig.prizes &&
          jetpackConfig.prizes.length >= 1
        );

      case STEP_3_GAMES:
        return (
          jetpackConfig !== null &&
          (!!jetpackConfig.games?.length ||
            !!jetpackConfig.activeEvents?.length)
        );

      case STEP_4_CTAS:
        return checkCTAsAreComplete(jetpackConfig);

      case STEP_5_MKTG_AUTO:
        return !!accountToDisplay?.businessUser?.stepVisits
          ?.marketingautomation;

      case STEP_6_POSA:
        return true;

      default:
        return true;
    }
  }

  const onClickStepRow = useCallback(
    (step: Step) => {
      history.push(
        buildPathWithTrackingId(
          `/configuration/${step.pathName}`,
          accountToDisplay?.trackingID
        )
      );
    },
    [accountToDisplay]
  );

  const plans = useSubscriptionPlansSelector();

  const plan = useMemo(() => {
    return plans?.find((p) => p._id === accountToDisplay?.saasPlanId) ?? null;
  }, [accountToDisplay]);

  const STEPS = useMemo(() => {
    return jetpackDashboardSteps.filter((step) =>
      filterStepsByFeatures(step, accountToDisplay?.saasFeatures, plan?.price)
    );
  }, [accountToDisplay, plan]);

  const isShowStepDiffusion = useMemo(() => {
    return jetpackDashboardSteps.every((step) => isStepCompleted(step));
  }, [jetpackConfig]);

  useEffect(() => {
    if (jetpackConfig == null) {
      return;
    }

    const obj: any = {};
    jetpackDashboardSteps.forEach((step) => {
      if (step.index === undefined || step.index === null) {
        return;
      }
      obj[step.index] = isStepCompleted(step);
    });
    setStepsCompletion(obj);
  }, [history.location, jetpackConfig]);

  return (
    <div className="d-flex">
      <div className="w-100">
        <div className="d-flex flex-column st-gap">
          <UpsellBanner />
          <div className="w-100 st-gap d-flex flex-column">
            {STEPS.map((step) => {
              return (
                <StepRow
                  id={`${TestsID.ONBOARDING_STEP_ROW}_${step.index}`}
                  onClick={() => {
                    onClickStepRow(step);
                  }}
                  key={step.index}
                  title={step.title}
                  isActive={stepsCompletion[step.index]}
                  svg={step.svg}
                  showCheckStatus={!isShowStepDiffusion}
                  noCheck={step.noCheck}
                />
              );
            })}
          </div>
          <KC_Separator size={KC_Sizes.m} />
        </div>
      </div>
    </div>
  );
}
